import { useEffect, useState } from 'react';
import { Project } from 'api/project';
import { Calendar } from 'api/timesheets/integration/calendar';
import { CalendarEventsWithMeta } from 'api/timesheets/integration/event';
import { Option } from 'components/atoms/select';
import { getTimesheetHandleError } from 'components/organisms/timesheets/utils';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useInvalidateCalendarIntegration } from 'queries/timesheets/calendar-integration-actions';
import {
  TimesheetCalendarIntegrationEntry,
  TimesheetEntry,
  TimesheetWeeklyRow,
} from '../../../types';
import { useProjectOptionsForTimesheet } from '../../add-adhoc';
import {
  CalenderIntegrationRowDataWithMeta,
  SelectedActivityRowData,
} from './activity-table';
import {
  getTimeFormatter,
  getTimesheetEntries,
  pushToServer,
  useGetLookupWeeklyRowByEntityId,
} from './utils';

interface UseGetConfigParams {
  eventsWithMeta?: CalendarEventsWithMeta;
  timesheetEntries?: TimesheetEntry[];
  timesheetWeekRows?: TimesheetWeeklyRow[];
  addIntegrationTaskToTimesheet: (
    tasks: TimesheetCalendarIntegrationEntry[],
  ) => void;
  selectedCalender?: Calendar;
  projects?: Project[];
}

const createRowData = (
  eventsWithMeta: UseGetConfigParams['eventsWithMeta'],
  projects: Option[],
  entityIdToProjectId,
): CalenderIntegrationRowDataWithMeta[] | undefined => {
  const getProjectById = (id) =>
    projects?.find((project) => project.value === id);

  return eventsWithMeta?.map(([meta, events]) => [
    meta,
    events.map((event) => {
      const autoPopulateProjectId = event.marked
        ? entityIdToProjectId[event.timeEntryId!]
        : event.suggestedProjectIds?.length === 1
        ? event.suggestedProjectIds[0]
        : undefined;

      return {
        ...event,
        projects: autoPopulateProjectId
          ? getProjectById(autoPopulateProjectId)
          : undefined,
        formattedTime: getTimeFormatter({
          ...event,
        }),
      };
    }),
  ]);
};

export const useGetTimesheetCalenderConfig = ({
  eventsWithMeta,
  addIntegrationTaskToTimesheet,
  projects,
  timesheetWeekRows,
}: UseGetConfigParams): {
  rowData: CalenderIntegrationRowDataWithMeta[] | undefined;
  addTaskListToTimesheet: (rows: SelectedActivityRowData[]) => Promise<void>;
  isUpdating: boolean;
} => {
  const { user } = useUser();
  const { userId } = user;
  const translate = useTranslate();
  const invalidateCalendarIntegration = useInvalidateCalendarIntegration();
  const projectOptions = useProjectOptionsForTimesheet(projects);
  const [isUpdating, setUpdating] = useState(false);

  const handleError = getTimesheetHandleError(user, translate);
  const entityIdToProjectId =
    useGetLookupWeeklyRowByEntityId(timesheetWeekRows);
  const [rowData, setRowData] =
    useState<CalenderIntegrationRowDataWithMeta[]>();

  useEffect(() => {
    setRowData(
      createRowData(eventsWithMeta, projectOptions, entityIdToProjectId),
    );
  }, [eventsWithMeta, entityIdToProjectId, projectOptions]);
  const addTaskListToTimesheet = async (rows: SelectedActivityRowData[]) => {
    const entries = getTimesheetEntries(rows, userId);

    setUpdating(true);
    const { fulfilled, rejected } = await pushToServer(entries, userId);

    setUpdating(false);

    rejected.forEach(({ reason, task }) =>
      handleError(reason, 'add', { task: task.activityName, day: task.day }),
    );
    addIntegrationTaskToTimesheet(fulfilled);
    await invalidateCalendarIntegration();
  };

  return { rowData, addTaskListToTimesheet, isUpdating };
};
